// Gemeinsames Buchungsfenster „Termin buchen“ (30.10.2026, backend/buchungsfenster, docs/buchungsfenster.md).
// Links: freie Lücken passend zur Dauer. Rechts: Art, Trainer (mit „Jeder Trainer: frühester freier Termin“), Kunde, Zeit,
// Konflikte VOR dem Buchen (Doppelt buchen nur mit Grund, nur Leitung) und danach die Karte „Was ist angekommen“.
// Die Seite rechnet nichts selbst: Lücken, Konflikte und die Karte kommen vom Server.
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock, Loader2, Zap } from 'lucide-react';
import api, { buchungsfensterAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ARTEN = [['pt', 'PT'], ['checkup', 'Check-Up'], ['gespraech', 'Mitarbeitergespräch']];
const DAUERN = [30, 45, 60, 90];
const STATUS_KLASSE = { ok: 'text-emerald-700 dark:text-emerald-400', offen: 'text-amber-700 dark:text-amber-400', nein: 'text-muted-foreground' };

const detailText = (e, std) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : d?.meldung || std;
};

export default function BuchungsFenster({ offen, onClose, datum: datum0, start: start0, art: art0 = 'pt', onGebucht }) {
  const { user } = useAuth();
  const leitung = ['admin', 'manager'].includes(user?.role);
  const [art, setArt] = useState(art0);
  const [datum, setDatum] = useState(datum0);
  const [dauer, setDauer] = useState(60);
  const [trainerListe, setTrainerListe] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [trainer, setTrainer] = useState('');
  const [person, setPerson] = useState('');
  const [q, setQ] = useState('');
  const [treffer, setTreffer] = useState([]);
  const [kunde, setKunde] = useState(null);
  const [notiz, setNotiz] = useState('');
  const [thema, setThema] = useState('');
  const [notizen, setNotizen] = useState('');
  const [melden, setMelden] = useState(true);
  const [start, setStart] = useState(start0 || '');
  const [lueck, setLueck] = useState(null);
  const [konflikte, setKonflikte] = useState([]);
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    if (!offen) return;
    setArt(art0); setDatum(datum0); setStart(start0 || ''); setErgebnis(null); setKonflikte([]); setGrund(''); setKunde(null); setQ('');
    buchungsfensterAPI.trainer().then((r) => { setTrainerListe(r.data.trainer || []); setTrainer((t) => t || (r.data.trainer?.[0]?.wert ?? '')); }).catch(() => {});
    if (leitung) buchungsfensterAPI.personen().then((r) => setPersonen(r.data.personen || [])).catch(() => {});
  }, [offen, art0, datum0, start0, leitung]);

  // Check-Up: nur wer Check-Ups macht („Wer macht Check-Ups?“ im Kalender, der Server prüft dasselbe beim Buchen)
  const liste = art === 'checkup' ? trainerListe.filter((t) => t.checkup) : trainerListe;
  useEffect(() => { if (art === 'checkup' && trainer && !liste.some((t) => t.wert === trainer)) setTrainer(''); }, [art, trainer, liste]);
  const ziel = art === 'gespraech' ? { person } : { trainer };
  const bereit = art === 'gespraech' ? !!person : (art === 'checkup' ? true : !!trainer);

  // Lücken neu holen, wenn sich Tag, Dauer, Trainer/Person oder Kunde ändern
  useEffect(() => {
    if (!offen || !bereit || !datum) { setLueck(null); return; }
    let aktuell = true;
    buchungsfensterAPI.luecken({ art, datum, dauer, ...ziel, kunde_id: kunde?.id || undefined })
      .then((r) => { if (aktuell) setLueck(r.data); })
      .catch((e) => { if (aktuell) { setLueck(null); toast.error(detailText(e, 'Lücken nicht geladen')); } });
    return () => { aktuell = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, art, datum, dauer, trainer, person, kunde?.id, bereit, ergebnis]);

  const koerper = useCallback(() => ({
    art, datum, start, dauer, ...ziel, kunde_id: kunde?.id || undefined, kunde_name: kunde?.name || undefined,
    notiz: notiz || undefined, thema: thema || undefined, notizen: notizen || undefined, melden,
    doppelt_grund: grund.trim() || undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [art, datum, start, dauer, trainer, person, kunde, notiz, thema, notizen, melden, grund]);

  // Konflikte VOR dem Buchen
  useEffect(() => {
    if (!offen || !start || !bereit || (art !== 'gespraech' && art !== 'checkup' && !trainer) || (art === 'checkup' && !kunde)) { setKonflikte([]); return; }
    let aktuell = true;
    buchungsfensterAPI.pruefen(koerper()).then((r) => { if (aktuell) setKonflikte(r.data.konflikte || []); }).catch(() => { if (aktuell) setKonflikte([]); });
    return () => { aktuell = false; };
  }, [offen, start, datum, dauer, trainer, person, kunde, art, bereit, koerper]);

  const suchen = async (wert) => {
    setQ(wert); setKunde(null);
    if (wert.trim().length < 2) { setTreffer([]); return; }
    try { const r = await api.get('/checkins/suche', { params: { q: wert.trim(), limit: 6 } }); setTreffer(r.data.treffer || []); } catch { setTreffer([]); }
  };

  const frueh = async () => {
    setLaeuft(true);
    try {
      const r = await buchungsfensterAPI.fruehester({ art: art === 'checkup' ? 'checkup' : 'pt', dauer, ab: datum, kunde_id: kunde?.id || undefined });
      setTrainer(r.data.wert); setDatum(r.data.datum); setStart(r.data.start);
      toast.success(`${r.data.trainer}: ${r.data.datum.split('-').reverse().slice(0, 2).join('.')}. ${r.data.start} Uhr`);
    } catch (e) { toast.error(detailText(e, 'Kein freier Termin gefunden')); } finally { setLaeuft(false); }
  };

  const buchen = async () => {
    if (!start) { toast.error('Bitte eine Lücke und eine Startzeit wählen'); return; }
    if (art !== 'gespraech' && art !== 'checkup' && !kunde && !q.trim()) { toast.error('Bitte den Kunden angeben'); return; }
    if (art === 'checkup' && !kunde) { toast.error('Bitte die Person wählen'); return; }
    setLaeuft(true);
    try {
      const body = koerper();
      if (art === 'pt' && !kunde) body.kunde_name = q.trim();
      const r = await buchungsfensterAPI.buchen(body);
      setErgebnis(r.data.ergebnis); onGebucht?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.konflikte) setKonflikte(d.konflikte);
      toast.error(detailText(e, 'Nicht gebucht'));
    } finally { setLaeuft(false); }
  };

  const wochentag = datum ? new Date(`${datum}T12:00`).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit' }) : '';
  const grundNoetig = konflikte.length > 0 && leitung;

  return (
    <Dialog open={offen} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="buchungsfenster">
        <DialogHeader><DialogTitle>Termin buchen</DialogTitle></DialogHeader>
        {ergebnis ? (
          <div className="space-y-3" data-testid="bf-ergebnis">
            <p className="text-sm font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-600" />Gebucht — was angekommen ist</p>
            <ul className="space-y-1.5">
              {ergebnis.zeilen.map((z, i) => (
                <li key={i} className="rounded-lg border border-border/60 p-2.5 text-sm">
                  <span className={`font-medium ${STATUS_KLASSE[z.status]}`}>{z.was}</span>
                  <span className="text-muted-foreground"> · {z.text}</span>
                </li>
              ))}
            </ul>
            <div className="flex gap-2"><Button size="sm" onClick={onClose}>Fertig</Button>
              <Button size="sm" variant="outline" onClick={() => { setErgebnis(null); setStart(''); }}>Noch einen buchen</Button></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-[1fr_1.2fr] gap-4">
            {/* Links: freie Lücken */}
            <div className="space-y-2" data-testid="bf-luecken">
              <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Freie Lücken · {wochentag} · {dauer} Min.</p>
              {!bereit && (art === 'gespraech' || liste.length > 0) && <p className="text-xs text-muted-foreground">{art === 'gespraech' ? 'Erst eine Person wählen.' : 'Erst einen Trainer wählen.'}</p>}
              {bereit && lueck && lueck.kein_arbeitstag && <p className="text-xs text-amber-700">Nicht verfügbar: {lueck.kein_arbeitstag}</p>}
              {bereit && lueck && !lueck.kein_arbeitstag && lueck.luecken.length === 0 && <p className="text-xs text-muted-foreground">Keine Lücke für {dauer} Minuten an diesem Tag.</p>}
              {lueck?.luecken.map((l) => (
                <div key={l.von} className={`rounded-lg border p-2 ${start && l.starts.includes(start) ? 'border-marke bg-marke/5' : 'border-border/60'}`}>
                  <button type="button" className="text-left text-sm font-medium w-full" onClick={() => setStart(l.starts[0])}>{l.text}</button>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {l.starts.slice(0, 12).map((s) => (
                      <button key={s} type="button" onClick={() => setStart(s)}
                        className={`px-1.5 h-6 rounded border text-[11px] ${start === s ? 'bg-marke text-white border-marke' : 'border-border'}`}>{s}</button>
                    ))}
                  </div>
                </div>
              ))}
              {lueck?.belegt?.length > 0 && (
                <details className="text-[11px] text-muted-foreground"><summary className="cursor-pointer">Schon belegt ({lueck.belegt.length})</summary>
                  {lueck.belegt.map((b, i) => <p key={i}>{b.start}–{b.ende} · {b.titel}</p>)}</details>
              )}
            </div>

            {/* Rechts: Angaben */}
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-3 rounded-full border border-border bg-muted/40 p-0.5" data-testid="bf-art">
                {ARTEN.filter(([k]) => k !== 'gespraech' || leitung).map(([k, l]) => (
                  <button key={k} type="button" onClick={() => { setArt(k); setStart(''); }}
                    className={`rounded-full py-1 ${art === k ? 'bg-card shadow-sm font-medium' : 'text-muted-foreground'}`}>{l}</button>
                ))}
              </div>
              <div className="flex gap-2 items-center">
                <Input type="date" value={datum || ''} onChange={(e) => { setDatum(e.target.value); setStart(''); }} className="h-8 text-xs w-40" />
                <div className="flex gap-1">{DAUERN.map((d) => (
                  <button key={d} type="button" onClick={() => { setDauer(d); setStart(''); }}
                    className={`px-2 h-8 rounded border text-xs ${dauer === d ? 'bg-marke text-white border-marke' : 'border-border'}`}>{d}</button>))}</div>
              </div>
              {art === 'gespraech' ? (
                <select value={person} onChange={(e) => { setPerson(e.target.value); setStart(''); }} className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="bf-person">
                  <option value="">Person aus dem Team wählen …</option>
                  {personen.map((p) => <option key={p.wert} value={p.wert}>{p.name} · {p.art}</option>)}
                </select>
              ) : (
                <div className="flex gap-1.5">
                  <select value={trainer} onChange={(e) => { setTrainer(e.target.value); setStart(''); }} className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs" data-testid="bf-trainer">
                    {art === 'checkup' && <option value="">Kein Wunschtrainer (Team übernimmt)</option>}
                    {art !== 'checkup' && !liste.length && <option value="">Noch kein Trainer da</option>}
                    {liste.map((t) => <option key={t.wert} value={t.wert}>{t.name}</option>)}
                  </select>
                  <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={frueh} disabled={laeuft || !liste.length} data-testid="bf-frueh">
                    <Zap className="w-3 h-3" />Jeder Trainer: frühester freier Termin</Button>
                </div>
              )}
              {art === 'pt' && !trainerListe.length && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400" data-testid="bf-kein-trainer">Noch kein Trainer da. Trainer legst du hier im Kalender unter „Trainer-Links“ an.</p>)}
              {art === 'checkup' && !liste.length && (
                <p className="text-[11px] text-muted-foreground" data-testid="bf-kein-checkup-trainer">Unter „Wer macht Check-Ups?“ steht noch niemand mit App-Konto. Der Check-Up geht ans Team.</p>)}
              {art !== 'gespraech' && (
                kunde ? (
                  <div className="flex items-center justify-between rounded-md bg-muted/50 px-2 py-1.5"><span className="font-medium">{kunde.name}</span>
                    <button type="button" className="text-[11px] text-muted-foreground hover:underline" onClick={() => { setKunde(null); setQ(''); }}>ändern</button></div>
                ) : (
                  <>
                    <Input value={q} onChange={(e) => suchen(e.target.value)} placeholder={art === 'pt' ? 'Kunde suchen (oder Namen eintippen) …' : 'Mitglied suchen …'} className="h-8 text-xs" data-testid="bf-kunde" />
                    {treffer.map((p) => <button key={p.id} type="button" onClick={() => { setKunde(p); setTreffer([]); }} className="w-full text-left rounded-md px-2 py-1 hover:bg-muted">{p.name}</button>)}
                    {art === 'pt' && q.trim().length > 1 && !kunde && <p className="text-[11px] text-muted-foreground">Ohne verknüpften Kunden prüft das Fenster nur den Trainer.</p>}
                  </>
                )
              )}
              {art === 'gespraech' ? (
                <>
                  <Input value={thema} onChange={(e) => setThema(e.target.value)} placeholder="Thema (nur Leitung sieht es)" className="h-8 text-xs" />
                  <textarea value={notizen} onChange={(e) => setNotizen(e.target.value)} placeholder="Notizen (nur Leitung)" rows={2} className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs" />
                </>
              ) : <Input value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Notiz (optional)" className="h-8 text-xs" />}
              {art === 'checkup' && (
                <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><input type="checkbox" checked={melden} onChange={(e) => setMelden(e.target.checked)} /> Mitglied per Push und Mail benachrichtigen</label>
              )}

              {konflikte.length > 0 && (
                <div className="rounded-lg border border-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2.5 space-y-1" data-testid="bf-konflikte">
                  <p className="font-semibold flex items-center gap-1 text-amber-900 dark:text-amber-200"><AlertTriangle className="w-3.5 h-3.5" />Achtung</p>
                  {konflikte.map((k, i) => <p key={i}>{k.text}</p>)}
                  {grundNoetig ? (
                    <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Grund fürs Doppelbuchen (mind. 5 Zeichen)" className="h-8 text-xs" data-testid="bf-grund" />
                  ) : <p className="text-muted-foreground">Doppelt buchen darf nur die Leitung. Bitte eine freie Lücke wählen.</p>}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button size="sm" className="h-8 text-xs gap-1" onClick={buchen} disabled={laeuft || !start || (konflikte.length > 0 && (!leitung || grund.trim().length < 5))} data-testid="bf-buchen">
                  {laeuft && <Loader2 className="w-3 h-3 animate-spin" />}{start ? `Buchen · ${start} Uhr` : 'Erst eine Lücke wählen'}</Button>
                <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={onClose}>Abbrechen</Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
